import {Injectable} from '@angular/core';
import {OAuthService} from 'angular-oauth2-oidc';
import { HttpHeaders, HttpClient } from '@angular/common/http';
import {environment} from '../../environments/environment';
import {Observable} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {HttpErrorHandlerService, HandleError} from './http-error-handling.service';
import {PricingOutput} from './rate.service';
import {Decimal} from 'decimal.js';
import {stringify} from 'query-string';

const httpOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json',
    'Authorization': 'my-auth-token'
  })
};

@Injectable()
export class SalesService {
  private handleError: HandleError;

  constructor(private oauthService: OAuthService,
              private http: HttpClient,
              httpErrorHandler: HttpErrorHandlerService) {
    httpOptions.headers = httpOptions.headers.set('Authorization', this.oauthService.authorizationHeader());
    this.handleError = httpErrorHandler.createHandleError('SalesService');
  }

  retrieveBrokerAdditionals(partyRoleId): Observable<any> {
    return this.http
      .get<any[]>(`${environment.api.sales}/party-role/${partyRoleId}/brokers`, httpOptions)
      .pipe(
        catchError(this.handleError('retrieveBrokerAdditionals', []))
      );
  }

  sendEmail(data): Observable<any> {
    return this.http
      .post<any[]>(`${environment.api.sales}/email`, data, httpOptions)
      .pipe(
        catchError(this.handleError('sendEmail', []))
      );
  }

  createQuote(data): Observable<any> {
    return this.http
      .post<any[]>(`${environment.api.sales}/quotes`, data, httpOptions)
      .pipe(
        catchError(this.handleError('createQuote', []))
      );
  }

  retrieveQuote(id): Observable<any> {
    return this.http
      .get<any[]>(`${environment.api.sales}/quotes/${id}`, httpOptions)
      .pipe(
        catchError(this.handleError('retrieveQuote', []))
      );
  }

  deleteQuote(id): Observable<any> {
    return this.http
      .delete<any[]>(`${environment.api.sales}/quotes/${id}`, httpOptions)
      .pipe(
        catchError(this.handleError('deleteQuoteS', []))
      );
  }

  listQuotes(brokerId, page): Observable<any> {
    return this.http
      .get<any[]>(`${environment.api.sales}/quotes/brokers/${brokerId}?p=${page}&s=created&sd=desc`, httpOptions)
      .pipe(
        catchError(this.handleError('listQuoteS', []))
      );
  }

  mapQuoteToApplication(data) {
    const sessionData = {
      'step-0': {
        'app-rate': data['app-rate'],
        'app-originator-broker': data['app-originator-broker'],
        'app-customer-details': data['app-customer-details'],
      },
      'step-2': {
        'app-loan-details-terms': {
          vin: data['app-quote-details']['vin'] || null,
          vehicle: data['app-quote-details']['vehicle'] || {},
          variant: data['app-quote-details']['variant'],
          purpose: data['app-quote-details']['purpose'],
          product: data['app-quote-details']['product'],
          repaymentFrequency: data['app-quote-details']['repaymentFrequency'],
          loanTermId: data['app-quote-details']['loanTermId'],
          loanTerm: data['app-quote-details']['loanTerm'],
          balloonAmount: data['app-quote-details']['balloonAmount'],
          balloonPercentage: data['app-quote-details']['balloonPercentage'],
          interestRate: (data['app-rate'] && data['app-rate']['output']) ? new Decimal(data['app-rate']['output']['TPSRateToBorrower']).mul(100).toNumber() : null,
        },
        'app-single-split-loan-amount': {
          purchaseRefinanceAmount: data['app-quote-details']['purchaseRefinanceAmount'],
          depositTradeAmount: data['app-quote-details']['depositTradeAmount'],
          subTotal: data['app-quote-details']['subTotal'],
          originationFeeCheck: data['app-quote-details']['originationFeeCheck'],
          originationFee: data['app-quote-details']['originationFee'],
          applicationFee: data['app-quote-details']['applicationFee'],
          subtotalWithFees: data['app-quote-details']['subtotalWithFees'],
          brokerageFeeCheck: data['app-quote-details']['brokerageFeeCheck'],
          brokerageFeeCapitalizedCheck: data['app-quote-details']['brokerageFeeCapitalizedCheck'],
          brokerageFeePercentage: data['app-quote-details']['brokerageFeePercentage'],
          brokerageFee: data['app-quote-details']['brokerageFee'],
          totalLoanAmount: data['app-quote-details']['totalLoanAmount'],
          insurances: data['app-quote-details']['insurances'],
        },
        'app-rate': data['app-rate']
      }
    };
    sessionData['step-2']['app-loan-details-terms']['vehicle']['text'] = data['app-quote-details']['description'] || data['app-quote-details']['vehicle'].text;
    return sessionData;
  }

  createPartial(data): Observable<any> {
    return this.http
      .post<any[]>(`${environment.api.sales}/wizard`, data, httpOptions)
      .pipe(
        catchError(this.handleError('createPartial', []))
      );
  }

  updatePartial(id, data): Observable<any> {
    return this.http
      .put<any[]>(`${environment.api.sales}/wizard/${id}`, data, httpOptions)
      .pipe(
        catchError(this.handleError('updatePartial', []))
      );
  }

  retrievePartial(id): Observable<any> {
    return this.http
      .get<any[]>(`${environment.api.sales}/wizard/${id}`, httpOptions)
      .pipe(
        catchError(this.handleError('retrievePartial', []))
      );
  }

  deletePartial(id): Observable<any> {
    return this.http
      .delete<any[]>(`${environment.api.sales}/wizard/${id}`, httpOptions)
      .pipe(
        catchError(this.handleError('deletePartial', []))
      );
  }

  listPartials(brokerId): Observable<any> {
    return this.http
      .get<any[]>(`${environment.api.sales}/wizard/brokers/${brokerId}?ps=100s=expiration&sd=-1`, httpOptions)
      .pipe(
        catchError(this.handleError('listPartial', []))
      );
  }

  retrieveLinkedAppCountByPartyDetails(params): Observable<any> {
    return this.http
      .get<any[]>(`${environment.api.sales}/linked/by-party-details?${stringify(params)}`, httpOptions)
      .pipe(
        catchError(this.handleError('retrieveLinkedAppCountByPartyDetails', 0))
      );
  }

  retrieveAdditionals(brokerId): Observable<any> {
    return this.http
      .get<any[]>(`${environment.api.sales}/party-role/${brokerId}/brokers`, httpOptions)
      .pipe(
        catchError(this.handleError('listPartial', []))
      );
  }

  /**
   * @param entity
   * @param type
   * @param valuerRequest
   * {
      "postcode": "string",
      "suburb": "string",
      "estimate": "string",
      "propertyType": "string"
    }
   * @returns
   */
  getValuers(entity, type, valuerRequest): Observable<any> {
    return this.http
      .post<any[]>(`${environment.api.collateral}/valuations/${entity}/valuer-look-up/${type}`, valuerRequest, httpOptions)
      .pipe(
        catchError(this.handleError('getValuers', []))
      );
  }

  runCalculation(calculator, data, version = null): Observable<any> {
    if (version) {
      httpOptions.headers = httpOptions.headers.set('x-api-version', version.toString());
    }

    return this.http.post<any[]>(`${environment.api.sales}/calculators/${calculator}`, data, httpOptions)
      .pipe(
        catchError(this.handleError('getNdiScore', []))
      );
  }

  /**
   * Fetches the fee type configuration for a particular fee
   * @param feeType
   * @param payload
   */
  getFeeTypeConfiguration(feeType: string, payload: any) {
    return this.http.post<any []>(`${environment.api.sales}/fees/${feeType}/configuration`, payload, httpOptions);
  }
}
